// =============================================================================
// Offline queue for field form submissions (design doc principle 4)
// =============================================================================
// When a field worker hits Submit on a Take 5 / JSEA / Incident form in the
// middle of a tower base with no signal, the submission is written to
// IndexedDB and the UI treats it as submitted. A background sync loop
// (listening to the `online` event and running every 30s when online)
// flushes the queue back to Supabase.
//
// Photos: on capture, the watermarked + original blobs are cached in
// IndexedDB. A `photo_finalise` queue entry tracks metadata. When we come
// back online, replay streams both blobs up via signed URLs and calls
// /photos/finalise on the api-gateway Worker.
//
// 10 Oct 2026 — HOP photos on the Nullarbor. James: "they took a lot yesterday
// that are now missing in ops ... whats our safety net for losing photos?"
// What was wrong, all of it in the HOP lane:
//   * A photo was only written to the phone AFTER an upload attempt failed.
//     On one bar the attempt does not fail, it HANGS (no timeout anywhere) —
//     so the photo sat in memory only, and leaving the page, locking the
//     phone or the browser dropping the tab lost it for good.
//   * One hung upload held the sync lock forever, so nothing behind it — on
//     any screen — synced until the app was reloaded.
//   * A queued photo whose saved copy had gone missing was dropped silently.
//   * Nothing in the app showed what was still waiting on the phone.
// Now: HOP photos are SAVED FIRST (HopCategoryScreen), then uploaded. They run
// in their own lane (flushHop) with real timeouts, so a stall can neither
// wedge the queue nor block forms/timesheets/receipts, whose lane is exactly
// as it was. A photo whose saved copy is gone is kept and flagged LOST, never
// dropped. queueSummary()/onQueueChange() feed the "waiting to upload" badge.
//
// 10 Oct 2026 (later) — the crew: "they cant see what they have uploaded new
// into which category". hopLocal(packId) answers that from THIS PHONE alone,
// signal or not: the photos still waiting (the queue) and the ones this phone
// has sent (a small text ledger in localStorage, kept 7 days). The HOP pack
// and category screens draw their "today on this phone" lines from it.
//
// 10 Oct 2026 (later still, mig 441) — TWO COPIES. A HOP photo's quick copy
// (under 3 MP) goes first; when it lands the queue item is not deleted, it
// becomes a "hop_full" item that owes the full-size copy, and the saved photo
// stays on the phone until that has gone too. Full copies only run when no
// quick copy is waiting, so they never hold up getting photos INTO Ops. They
// are counted apart (QueueSummary.full) — a full copy still on the phone is
// not a missing photo. storageEstimate() tells the badge how much room is left.
//
// 10 Oct 2026 (Al's phone: "One pending upload, he hits upload now and nothing
// happens, its a black photo") — a photo that cannot be READ (a broken or
// empty file) failed every try, and because the lane stops at the first
// failure it also held up every photo queued behind it. Now the lane only
// stops for a SIGNAL failure. A photo that fails for its own reason is stepped
// over, and after 3 tries it is marked STUCK: left alone, shown on the badge
// with what went wrong, and the person can try it again, save a copy of the
// file, or remove it (listProblems / retryQueued / removeQueued).
//
// 1 Nov 2026 — the FORMS lane (forms, timesheets, receipts, diary, HSE photos)
// catches up with the photo lane. Before: every failure counted, so ten ticks
// of bad signal (five minutes) marked a form "can't upload" with GIVE_UP, and
// a GIVE_UP item was still retried every 30 s forever. Now a signal failure
// never counts against the item — the pass stops and the next tick tries
// again; only a failure of the item's own (Ops refused it, the file is gone)
// counts, and after STUCK_AFTER of those it is marked stuck, skipped until
// someone presses Try again or Remove on the badge. Larry's vehicle accident
// report on one bar is exactly this case.
// =============================================================================
import { openDB, type IDBPDatabase } from "idb";
import { supabase, apiFetch } from "./supabase";

const DB_NAME = "coretel-offline";
const DB_VERSION = 1;

interface QueuedItem {
  id?: number;
  // T-401 — receipt_capture / diary_entry / hop_asset added: every field
  // capture path now survives no-signal instead of erroring (receipts used
  // to DELETE the photo on failure).
  kind: "form_submission" | "photo_finalise" | "timesheet_entry" | "swms_acceptance"
      | "receipt_capture" | "diary_entry" | "hop_asset" | "hse_photo"
      | "hop_full";   // mig 441 — quick copy is in Ops; the full-size copy is still owed
  payload: any;
  queued_at: string;
  attempts: number;
  last_error?: string;
  /** 10 Oct 2026 — the saved photo behind this item is gone; it can never
   *  upload. Kept (not deleted) so the badge can say so. */
  dead?: boolean;
  /** 10 Oct 2026 — failed 3 times for a reason that is not signal (the file
   *  cannot be read, Ops refused it). Skipped until someone retries or removes it. */
  stuck?: boolean;
}

// A failure the NEXT photo would hit too (no/weak signal, server down, signed
// out) — as opposed to something wrong with this one photo.
function isSignalFailure(msg: string): boolean {
  return /timed out|timed_out|failed to fetch|networkerror|network|load failed|not_authenticated|aborted|\((5\d\d|401|403|429)\)|R2 upload failed: (5|401|403|429)/i.test(msg);
}
const STUCK_AFTER = 3;

let dbPromise: Promise<IDBPDatabase> | null = null;

function getDb() {
  if (dbPromise) return dbPromise;
  dbPromise = openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains("queue")) {
        const store = db.createObjectStore("queue", { keyPath: "id", autoIncrement: true });
        store.createIndex("kind", "kind");
        store.createIndex("queued_at", "queued_at");
      }
      if (!db.objectStoreNames.contains("photos")) {
        db.createObjectStore("photos", { keyPath: "local_id" });
      }
    },
  });
  return dbPromise;
}

// -----------------------------------------------------------------------------
// Public API
// -----------------------------------------------------------------------------
export async function queueItem(kind: QueuedItem["kind"], payload: any): Promise<number> {
  const db = await getDb();
  const id = await db.add("queue", {
    kind, payload,
    queued_at: new Date().toISOString(),
    attempts: 0,
  } satisfies Omit<QueuedItem, "id">);
  notify();
  return id as number;
}

// -----------------------------------------------------------------------------
// 10 Oct 2026 — what is still on this phone (for the badge), and the hooks the
// HOP screen uses to save a photo first and upload it second.
// -----------------------------------------------------------------------------
const listeners = new Set<() => void>();
function notify() { for (const l of listeners) { try { l(); } catch { /* a listener must not break the queue */ } } }
/** Call back whenever the queue changes. Returns the unsubscribe. */
export function onQueueChange(cb: () => void): () => void {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

export interface QueueSummary {
  waiting: number;        // everything still to upload (not lost, not stuck, not a full-size copy)
  stuck: number;          // will not upload without someone looking at it
  full: number;           // mig 441 — full-size copies still to follow (photo already in Ops)
  photos: number;         // of those, HOP photos
  lost: number;           // HOP photos whose saved copy is gone
  oldest: string | null;  // ISO time of the oldest waiting item
  lastError: string | null;
}
export async function queueSummary(): Promise<QueueSummary> {
  const db = await getDb();
  const items = await db.getAll("queue") as QueuedItem[];
  const full = items.filter(i => i.kind === "hop_full").length;
  const isStuck = (i: QueuedItem) => !!i.stuck || String(i.last_error ?? "").startsWith("GIVE_UP");
  const live = items.filter(i => !i.dead && !isStuck(i) && i.kind !== "hop_full");
  const errs = live.filter(i => i.last_error).sort((a, b) => (b.attempts - a.attempts));
  return {
    waiting: live.length,
    stuck: items.filter(i => !i.dead && isStuck(i) && i.kind !== "hop_full").length,
    full,
    photos: live.filter(i => i.kind === "hop_asset").length,
    lost: items.filter(i => i.dead).length,
    oldest: live.length ? live.map(i => i.queued_at).sort()[0] : null,
    lastError: errs[0]?.last_error ?? null,
  };
}
export interface QueueProblem { id: number; kind: string; label: string; queued_at: string; reason: string; local_id: string | null }
const KIND_LABEL: Record<string, string> = {
  hop_asset: "HOP photo", receipt_capture: "Receipt", diary_entry: "Site diary entry", form_submission: "Form",
  timesheet_entry: "Timesheet entry", swms_acceptance: "SWMS sign-on", photo_finalise: "Photo", hse_photo: "HSE photo",
};
function plainReason(e: string | undefined): string {
  const m = String(e ?? "").replace(/^GIVE_UP:\s*/, "");
  if (/_GIVE_UP$/.test(m)) return "The saved copy of the file is no longer on this phone.";
  if (!m || m === "[object Event]" || /decode|canvas|image/i.test(m)) return "The file could not be read — it may be empty or damaged.";
  return m;
}
/** Items that will not upload by themselves. */
export async function listProblems(): Promise<QueueProblem[]> {
  const db = await getDb();
  const items = await db.getAll("queue") as QueuedItem[];
  return items
    // stuck, given up, or failing for more than 12 hours (whatever the reason —
    // so there is always a way to clear something that will not go)
    .filter(i => !i.dead && i.kind !== "hop_full" && (i.stuck || String(i.last_error ?? "").startsWith("GIVE_UP")
      || (!!i.last_error && Date.now() - new Date(i.queued_at).getTime() > 12 * 3600_000)))
    .map(i => ({
      id: i.id!, kind: i.kind,
      label: `${KIND_LABEL[i.kind] ?? i.kind}${i.payload?.file_name ? ` · ${i.payload.file_name}` : ""}`,
      queued_at: i.queued_at, reason: plainReason(i.last_error),
      local_id: i.payload?.local_id ? String(i.payload.local_id) : null,
    }));
}
/** Put a stuck item back in line and try now. */
export async function retryQueued(id: number): Promise<void> {
  const db = await getDb();
  const i = await db.get("queue", id) as QueuedItem | undefined;
  if (!i) return;
  await db.put("queue", { ...i, stuck: false, attempts: 0, last_error: undefined });
  notify();
  await syncNow();
}
/** Take an item off this phone for good (and its saved photo). */
export async function removeQueued(id: number): Promise<void> {
  const db = await getDb();
  const i = await db.get("queue", id) as QueuedItem | undefined;
  if (!i) return;
  await db.delete("queue", id);
  const lid = i.payload?.local_id;
  if (lid) { for (const k of [lid, `${lid}_wm`, `${lid}_orig`]) await db.delete("photos", String(k)).catch(() => {}); }
  notify();
}

/** Clear the "lost" flags once someone has read them. */
export async function dismissLost(): Promise<void> {
  const db = await getDb();
  const items = await db.getAll("queue") as QueuedItem[];
  for (const i of items) if (i.dead) await db.delete("queue", i.id!);
  notify();
}

// A HOP photo the screen is uploading right now — the sync loop leaves it
// alone so the same photo is never sent twice at once.
const claimedHop = new Set<string>();
export function claimHopUpload(local_id: string) { claimedHop.add(local_id); }
export function releaseHopUpload(local_id: string) { claimedHop.delete(local_id); }
/** The screen uploaded the quick copy itself. If a full-size copy is owed
 *  (mig 441) the item stays, as a hop_full, and so does the saved photo;
 *  otherwise it comes off the queue and the copy is dropped. */
export async function completeHopUpload(queueId: number, local_id: string, full?: { asset_id: string }): Promise<void> {
  const db = await getDb();
  const item = await db.get("queue", queueId) as QueuedItem | undefined;
  if (item) ledgerAdd(item);
  if (item && full?.asset_id) {
    await db.put("queue", toFullItem(item, full.asset_id));
  } else {
    await db.delete("queue", queueId);
    await db.delete("photos", local_id);
  }
  notify();
}
function toFullItem(item: QueuedItem, asset_id: string): QueuedItem {
  return { id: item.id, kind: "hop_full", payload: { ...item.payload, asset_id }, queued_at: item.queued_at, attempts: 0 };
}

/** How much room this phone gives Ops for saved photos. Null where the
 *  browser will not say. */
export async function storageEstimate(): Promise<{ usedMb: number; quotaMb: number; pct: number } | null> {
  try {
    const e = await (navigator as any).storage?.estimate?.();
    if (!e || !e.quota) return null;
    const usedMb = Math.round((e.usage ?? 0) / 1048576), quotaMb = Math.round(e.quota / 1048576);
    return { usedMb, quotaMb, pct: Math.round(((e.usage ?? 0) / e.quota) * 100) };
  } catch { return null; }
}
/** Merge a patch into a queued HOP item's upload args (the GPS fix arrives
 *  a few seconds after the photo is saved). */
export async function patchHopArgs(queueId: number, patch: Record<string, unknown>): Promise<void> {
  const db = await getDb();
  const item = await db.get("queue", queueId) as QueuedItem | undefined;
  if (!item) return;
  await db.put("queue", { ...item, payload: { ...item.payload, args: { ...(item.payload?.args ?? {}), ...patch } } });
}

// -----------------------------------------------------------------------------
// 10 Oct 2026 — what THIS PHONE knows about a HOP pack, with or without signal.
// The ledger is text only (ids + times), so it costs nothing and survives a
// reload. It is a convenience for the crew, never the record: the record is
// hop_assets in Ops. A failed write here must never stop an upload.
// -----------------------------------------------------------------------------
const LEDGER_KEY = "coretel-hop-ledger-v1";
const LEDGER_DAYS = 7;
export interface HopSentRow { id: string; pack: string; cat: string; sub: string | null; inst: string | null; taken: string; sent: string }
export interface HopWaitingRow {
  local_id: string; pack: string; cat: string; sub: string | null; inst: string | null;
  file_name: string; taken: string; lost: boolean; tries: number; stuck: boolean;
}
function ledgerRead(): HopSentRow[] {
  try {
    const rows = JSON.parse(localStorage.getItem(LEDGER_KEY) || "[]");
    return Array.isArray(rows) ? rows : [];
  } catch { return []; }
}
function ledgerAdd(item: QueuedItem) {
  try {
    const a = item.payload?.args ?? {};
    if (!a.packId || !a.packCategoryId) return;
    const cutoff = Date.now() - LEDGER_DAYS * 864e5;
    const id = String(item.payload?.local_id ?? item.id ?? "");
    const rows = ledgerRead().filter(r => r.id !== id && new Date(r.sent).getTime() > cutoff);
    rows.push({
      id, pack: a.packId, cat: a.packCategoryId, sub: a.packSubcategoryId ?? null, inst: a.instanceId ?? null,
      taken: a.capturedAtIso ?? item.queued_at, sent: new Date().toISOString(),
    });
    localStorage.setItem(LEDGER_KEY, JSON.stringify(rows.slice(-3000)));
  } catch { /* storage full or blocked — the upload itself is what matters */ }
}
/** Photos for one pack: still on this phone, and sent from this phone. */
export async function hopLocal(packId: string): Promise<{ waiting: HopWaitingRow[]; sent: HopSentRow[]; full: number }> {
  const db = await getDb();
  const everything = await db.getAll("queue") as QueuedItem[];
  const full = everything.filter(i => i.kind === "hop_full" && i.payload?.args?.packId === packId).length;
  const items = everything
    .filter(i => i.kind === "hop_asset" && i.payload?.args?.packId === packId)
    .sort((x, y) => x.queued_at.localeCompare(y.queued_at));
  return {
    waiting: items.map(i => ({
      local_id: String(i.payload.local_id), pack: packId,
      cat: i.payload.args.packCategoryId, sub: i.payload.args.packSubcategoryId ?? null, inst: i.payload.args.instanceId ?? null,
      file_name: String(i.payload.file_name ?? "photo"),
      taken: i.payload.args.capturedAtIso ?? i.queued_at,
      lost: !!i.dead, tries: i.attempts ?? 0, stuck: !!i.stuck,
    })),
    sent: ledgerRead().filter(r => r.pack === packId),
    full,
  };
}

export async function cachePhoto(local_id: string, blob: Blob, meta: Record<string, unknown>): Promise<void> {
  const db = await getDb();
  await db.put("photos", { local_id, blob, meta, queued_at: new Date().toISOString() });
}

export async function getCachedPhoto(local_id: string): Promise<{ blob: Blob; meta: Record<string, unknown> } | null> {
  const db = await getDb();
  const row = await db.get("photos", local_id);
  return row ?? null;
}

export async function removeCachedPhoto(local_id: string): Promise<void> {
  const db = await getDb();
  await db.delete("photos", local_id);
}

export async function queueLength(): Promise<number> {
  const db = await getDb();
  return db.count("queue");
}

// -----------------------------------------------------------------------------
// Sync loop
// -----------------------------------------------------------------------------
let syncing = false;

export async function flushQueue(): Promise<{ synced: number; failed: number }> {
  if (syncing) return { synced: 0, failed: 0 };
  if (!navigator.onLine) return { synced: 0, failed: 0 };
  syncing = true;

  try {
    const db = await getDb();
    // 10 Oct 2026 — HOP photos have their own lane (flushHop below). This
    // lane is otherwise unchanged.
    // 1 Nov 2026 — stuck items (and the old GIVE_UP ones) wait for Try again;
    // oldest first so a form is never queued behind a newer one.
    const items = (await db.getAll("queue") as QueuedItem[])
      .filter(i => i.kind !== "hop_asset" && i.kind !== "hop_full" && !i.dead && !i.stuck && !String(i.last_error ?? "").startsWith("GIVE_UP"))
      .sort((a, b) => a.queued_at.localeCompare(b.queued_at));
    let synced = 0, failed = 0;

    for (const item of items) {
      try {
        await replay(item);
        await db.delete("queue", item.id!);
        synced++;
      } catch (err: any) {
        failed++;
        const msg = err?.message || String(err);
        // a saved file that is gone can never upload — its own fault, every time
        const gone = /_GIVE_UP$/.test(msg);
        const signal = !gone && isSignalFailure(msg);
        // 1 Nov 2026 — only a failure of THIS item counts toward being stuck
        const attempts = (item.attempts ?? 0) + (signal ? 0 : 1);
        await db.put("queue", {
          ...item,
          attempts,
          last_error: msg,
          ...(!signal && attempts >= STUCK_AFTER ? { stuck: true } : {}),
        });
        // bad signal → the next item would hit the same wall; stop and let
        // the next tick try again. Something wrong with this one → step over it.
        if (signal) break;
      }
    }
    if (synced || failed) notify();
    return { synced, failed };
  } finally {
    syncing = false;
  }
}

// -----------------------------------------------------------------------------
// 10 Oct 2026 — the HOP photo lane. One photo at a time, oldest first. Every
// network step inside uploadHopAsset has a timeout and is aborted when it
// trips, and the outer guard below is the belt to those braces: nothing can
// hold this lane for more than HOP_ITEM_MAX_MS. A failure stops the pass (the
// next photo would hit the same dead signal) and the next tick tries again.
// -----------------------------------------------------------------------------
const HOP_ITEM_MAX_MS = 12 * 60_000;
let syncingHop = false;

export async function flushHop(): Promise<{ synced: number; failed: number }> {
  if (syncingHop) return { synced: 0, failed: 0 };
  if (!navigator.onLine) return { synced: 0, failed: 0 };
  syncingHop = true;
  let synced = 0, failed = 0;
  try {
    const db = await getDb();
    const all = (await db.getAll("queue") as QueuedItem[])
      .filter(i => (i.kind === "hop_asset" || i.kind === "hop_full") && !i.dead && !i.stuck)
      .sort((a, b) => a.queued_at.localeCompare(b.queued_at));
    // mig 441 — every quick copy first (that is what puts a photo in Ops),
    // then the full-size copies. Full copies made in this pass are picked up
    // on the next one.
    const items = [...all.filter(i => i.kind === "hop_asset"), ...all.filter(i => i.kind === "hop_full")];

    for (const item of items) {
      const local_id = String(item.payload?.local_id ?? "");
      if (claimedHop.has(local_id)) {
        if (item.kind === "hop_asset") continue;   // the screen is on it
        break;                                      // a new photo is going up — full copies wait
      }
      claimedHop.add(local_id);
      const isFull = item.kind === "hop_full";
      try {
        let timer: ReturnType<typeof setTimeout> | undefined;
        const res = await Promise.race([
          isFull ? replayHopFull(item.payload) : replayHopAsset(item.payload),
          new Promise<never>((_, rej) => { timer = setTimeout(() => rej(new Error("timed_out")), HOP_ITEM_MAX_MS); }),
        ]).finally(() => { if (timer) clearTimeout(timer); });
        if (!isFull) ledgerAdd(item);
        const owed = !isFull && (res as any)?.full_pending && (res as any)?.asset_id;
        if (owed) {
          // quick copy is in Ops; keep the saved photo for its full-size copy
          await db.put("queue", toFullItem(item, String((res as any).asset_id)));
        } else {
          // off the queue FIRST, then the saved copy: a crash between the two
          // leaves a stray copy (harmless), never a queue item with no photo.
          await db.delete("queue", item.id!);
          await db.delete("photos", local_id);
        }
        synced++;
        notify();
      } catch (err: any) {
        failed++;
        const msg = err?.message || String(err);
        const lost = msg.startsWith("PHOTO_LOST");
        const fresh = await db.get("queue", item.id!) as QueuedItem | undefined;
        if (fresh && isFull && lost) {
          // the full-size copy cannot be made, but the photo itself IS in Ops
          // (its quick copy) — nothing is lost, so nothing to flag.
          await db.delete("queue", item.id!);
        } else if (fresh) {
          // a full-size copy is never marked stuck — its photo is already in
          // Ops and a missing endpoint or a refusal just means "later".
          const signal = isFull || isSignalFailure(msg);
          // only a failure of THIS photo counts toward being stuck
          const attempts = (fresh.attempts ?? 0) + (signal || lost ? 0 : 1);
          await db.put("queue", {
            ...fresh,
            attempts,
            last_error: msg,
            ...(lost ? { dead: true } : {}),
            ...(!lost && !signal && attempts >= STUCK_AFTER ? { stuck: true } : {}),
          });
        }
        notify();
        // signal is bad → stop, try again next tick. Something wrong with this
        // one photo → step over it so it cannot hold up the ones behind it.
        if (!lost && (isFull || isSignalFailure(msg))) break;
      } finally {
        claimedHop.delete(local_id);
      }
    }
    return { synced, failed };
  } finally {
    syncingHop = false;
  }
}

async function replay(item: QueuedItem): Promise<void> {
  switch (item.kind) {
    case "form_submission": {
      // T-423: incident forms queued offline carry a client-minted
      // submission_id (retry-safe) + a promotion flag. Previously the
      // promotion step was silently dropped, so an incident reported from a
      // black spot NEVER became an incidents row — nobody was notified.
      const { __promote_incident, ...row } = (item.payload ?? {}) as any;
      const { error } = await supabase.from("form_submissions").insert(row);
      if (error && !dupOk(error)) throw error;
      if (__promote_incident && row.submission_id) {
        // RPC is idempotent as of mig 242 — a retry returns the existing incident.
        const { error: e2 } = await supabase.rpc("incident_from_submission", { p_submission_id: row.submission_id });
        if (e2) throw e2;   // keep the item queued so promotion is never lost
      }
      return;
    }
    case "timesheet_entry": {
      const { error } = await supabase.from("timesheet_entries").insert(item.payload);
      if (error) throw error;
      return;
    }
    case "swms_acceptance": {
      const { error } = await supabase.from("swms_acceptances").insert(item.payload);
      if (error) throw error;
      return;
    }
    case "photo_finalise": {
      await replayPhotoFinalise(item.payload);
      return;
    }
    case "receipt_capture": {
      await replayReceiptCapture(item.payload);
      return;
    }
    case "diary_entry": {
      await replayDiaryEntry(item.payload);
      return;
    }
    case "hop_asset": {
      // never reached since 10 Oct 2026 — flushQueue filters these out and
      // flushHop replays them. Left so an unknown caller still does the right thing.
      await replayHopAsset(item.payload);
      await removeCachedPhoto(item.payload.local_id);
      return;
    }
    case "hse_photo": {
      // T-423b — HSE evidence photo captured offline: blob is cached, the
      // storage path was pre-minted (already recorded in the submission's
      // field_values), so replay = upload to that exact path. upsert makes
      // retries harmless.
      const { cache_key, path } = item.payload as { cache_key: string; path: string };
      const cached = await getCachedPhoto(cache_key);
      if (!cached) return;                        // nothing to upload — drop
      const { error } = await supabase.storage
        .from("hse-evidence")
        // 1 Nov 2026 — a drawing (accident map, damage diagram) is a PNG; the content type follows the path
        .upload(path, cached.blob, { contentType: path.toLowerCase().endsWith(".png") ? "image/png" : "image/jpeg", upsert: true });
      if (error) throw error;
      await removeCachedPhoto(cache_key);
      return;
    }
    default:
      throw new Error(`unknown_kind:${(item as any).kind}`);
  }
}

// -----------------------------------------------------------------------------
// T-401 — receipt capture replay. Idempotent: receipt_id is client-minted so
// a retry that half-succeeded before just skips the done parts.
// -----------------------------------------------------------------------------
const dupOk = (err: any) =>
  err && (err.code === "23505" || /exist|duplicate/i.test(err.message ?? ""));

async function replayReceiptCapture(p: {
  local_id: string;
  receipt_id: string;
  storage_path: string;
  mime_type: string;
  size_bytes: number;
  row: Record<string, unknown>;
}) {
  const cached = await getCachedPhoto(p.local_id);
  if (!cached) throw new Error("receipt_blob_missing_GIVE_UP");

  const { error: upErr } = await supabase.storage.from("receipt-captures")
    .upload(p.storage_path, cached.blob, { contentType: p.mime_type, cacheControl: "3600", upsert: false });
  if (upErr && !dupOk(upErr)) throw upErr;   // "already exists" = prior retry got this far

  const { error: rowErr } = await supabase.from("pending_receipts").insert({
    receipt_id:   p.receipt_id,
    storage_path: p.storage_path,
    mime_type:    p.mime_type,
    size_bytes:   p.size_bytes,
    ...p.row,
  });
  if (rowErr && !dupOk(rowErr)) throw rowErr;

  // Fire-and-forget OCR, same as the online path (keepalive since 8 Oct 2026;
  // the worker's sweep catches anything that still never lands — mig 424).
  apiFetch("/ai/extract-document", {
    method: "POST",
    keepalive: true,
    body: JSON.stringify({ pending_receipt_id: p.receipt_id }),
  }).catch(() => { /* the sweep re-runs it */ });

  await removeCachedPhoto(p.local_id);
}

// -----------------------------------------------------------------------------
// T-401 — site diary replay. Entry row first (diary_id client-minted → dup-
// tolerant retries), then photos replayed through the REAL upload function
// (uploadDiaryPhoto) with the cached original file — watermark, GPS metadata,
// RLS user lookup all behave exactly like an online upload. Per-photo
// idempotency: each photo's cache is removed on success, and missing caches
// are skipped, so a partial replay resumes where it died without duplicates.
// -----------------------------------------------------------------------------
async function replayDiaryEntry(p: {
  diary_id: string;
  entry: Record<string, unknown>;       // full site_diary_entries insert (incl. is_first_entry as computed at capture)
  photos: {
    local_id: string;
    file_name: string;
    content_type: string;
    caption: string | null;
    kind: string | null;
    sort_order: number;
    precomputed_gps: { lat: number; lng: number } | null;
    skip_watermark: boolean;
  }[];
}) {
  const { error: entryErr } = await supabase.from("site_diary_entries")
    .insert({ diary_id: p.diary_id, ...p.entry });
  if (entryErr && !dupOk(entryErr)) throw entryErr;

  // Lazy import — keeps lib/ from importing feature code at module load.
  const { uploadDiaryPhoto } = await import("@/features/diary/diaryPhotoUpload");

  for (const ph of p.photos) {
    const cached = await getCachedPhoto(ph.local_id);
    if (!cached) continue;   // already replayed on a previous attempt
    const file = new File([cached.blob], ph.file_name, { type: ph.content_type });
    await uploadDiaryPhoto({
      diaryId:        p.diary_id,
      projectId:      String(p.entry.project_id),
      file,
      caption:        ph.caption,
      kind:           ph.kind ?? "general",
      sortOrder:      ph.sort_order,
      precomputedGps: ph.precomputed_gps,
      skipWatermark:  ph.skip_watermark,
    });
    await removeCachedPhoto(ph.local_id);
  }
}

// -----------------------------------------------------------------------------
// T-401 — HOP photo replay: same delegation pattern — cached original file
// through the real uploadHopAsset (sign → PUT → finalise-asset), so
// watermarking, GPS and the worker's asset row are identical to online.
// -----------------------------------------------------------------------------
async function replayHopAsset(p: {
  local_id: string;
  file_name: string;
  content_type: string;
  args: Record<string, unknown>;   // serialisable HopUploadArgs minus file/onProgress
}) {
  const cached = await getCachedPhoto(p.local_id);
  // 10 Oct 2026 — was `return` ("replayed previously"), which quietly threw
  // the queue item away. A queued photo with no saved copy is a LOST photo and
  // the crew need to be told (flushHop flags it; the badge shows it).
  if (!cached) throw new Error("PHOTO_LOST: the saved copy of " + p.file_name + " is no longer on this phone");
  const { uploadHopAsset } = await import("@/features/hop/hopUpload");
  const file = new File([cached.blob], p.file_name, { type: p.content_type });
  // the result tells flushHop whether a full-size copy is owed (mig 441)
  return await uploadHopAsset({ ...(p.args as any), file });
  // the saved copy is removed by flushHop AFTER the queue item is deleted
}

// mig 441 — the full-size copy of a photo whose quick copy is already in Ops.
async function replayHopFull(p: {
  local_id: string; file_name: string; content_type: string; asset_id: string;
  args: Record<string, unknown>;
}) {
  const cached = await getCachedPhoto(p.local_id);
  if (!cached) throw new Error("PHOTO_LOST: no saved copy for the full-size upload of " + p.file_name);
  const { uploadHopFull } = await import("@/features/hop/hopUpload");
  const file = new File([cached.blob], p.file_name, { type: p.content_type });
  return await uploadHopFull({ ...(p.args as any), file, assetId: p.asset_id });
}

async function replayPhotoFinalise(p: {
  local_id: string;
  pack_item_id?: string;
  pack_id?: string;
  project_code?: string;
  watermark_data: Record<string, string>;
  exif: Record<string, unknown>;
  captured_at: string;
  gps_lat?: number;
  gps_lng?: number;
  gps_accuracy_m?: number;
}) {
  const wm   = await getCachedPhoto(`${p.local_id}_wm`);
  const orig = await getCachedPhoto(`${p.local_id}_orig`);
  if (!wm || !orig) throw new Error("photo_blobs_missing_GIVE_UP");

  const baseKey = `projects/${p.project_code ?? "unassigned"}/acceptance-packs/${p.pack_id ?? "adhoc"}/${p.local_id}`;
  const wmKey   = `${baseKey}.jpg`;
  const origKey = `${baseKey}_original.jpg`;

  for (const [blob, key, ct] of [
    [wm.blob,   wmKey,   "image/jpeg"],
    [orig.blob, origKey, "image/jpeg"],
  ] as const) {
    const signRes = await apiFetch("/sign-upload", {
      method: "POST",
      body: JSON.stringify({ bucket: "coretel-prod", object_key: key, content_type: ct, expires_in_seconds: 900 }),
    });
    if (!signRes.ok) throw new Error(`sign_upload_${signRes.status}`);
    const { upload_url } = await signRes.json();
    const putRes = await fetch(upload_url, { method: "PUT", headers: { "Content-Type": ct }, body: blob });
    if (!putRes.ok) throw new Error(`r2_put_${putRes.status}`);
  }

  const finaliseRes = await apiFetch("/photos/finalise", {
    method: "POST",
    body: JSON.stringify({
      pack_item_id: p.pack_item_id,
      pack_id: p.pack_id,
      watermarked: { object_key: wmKey,   size_bytes: wm.blob.size },
      original:    { object_key: origKey, size_bytes: orig.blob.size },
      captured_at: p.captured_at,
      gps_lat: p.gps_lat, gps_lng: p.gps_lng, gps_accuracy_m: p.gps_accuracy_m,
      watermark_data: p.watermark_data,
      exif: p.exif,
      device_info: { ua: navigator.userAgent, platform: navigator.platform, replayed: true },
    }),
  });
  if (!finaliseRes.ok) throw new Error(`finalise_${finaliseRes.status}`);

  await removeCachedPhoto(`${p.local_id}_wm`);
  await removeCachedPhoto(`${p.local_id}_orig`);
}

export function startOfflineSync() {
  const tick = () => {
    flushQueue().catch(console.error);
    flushHop().catch(console.error);     // 10 Oct 2026 — HOP photos, own lane
  };
  // 10 Oct 2026 — ask the browser not to clear this site's saved photos when
  // the phone runs low on space. Best effort; some browsers just say no.
  try { (navigator as any).storage?.persist?.().catch(() => {}); } catch { /* not supported */ }
  tick();
  const interval = setInterval(tick, 30_000);
  window.addEventListener("online", tick);
  // Opening the phone at the roadhouse: go now, don't wait for the next tick.
  const onVisible = () => { if (document.visibilityState === "visible") tick(); };
  document.addEventListener("visibilitychange", onVisible);
  return () => {
    clearInterval(interval);
    window.removeEventListener("online", tick);
    document.removeEventListener("visibilitychange", onVisible);
  };
}

/** "Upload now" on the badge. */
export async function syncNow(): Promise<void> {
  await Promise.allSettled([flushQueue(), flushHop()]);
  notify();
}
